import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { User } from 'src/features/auth/types';
import { queryClient } from 'src/lib/react-query';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { userMessages } from 'src/features/users/utils/messages';
import { toast } from 'src/hooks/useToast';
import { useChangeUserPassword } from 'src/features/users/api/useChangeUserPassword';
import { changeUserPasswordFormSchema, ChangeUserPasswordFormSchemaValues } from 'src/features/users/utils/formSchema';
import ChangeUserPasswordForm from 'src/features/users/components/forms/ChangeUserPasswordForm';
import { ICON_MAP } from 'src/utils/constants';
import { LIST_USERS_RESOURCE } from 'src/features/users/api/useListUsers';

interface UserFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedUser: User;
    children: React.ReactNode;
}

const defaultValues = {
    password_confirm: '',
};
function ChangeUserPasswordFormModal<TData>({ selectedUser, children }: UserFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const onError = useOnError();
    const { mutate, isPending } = useChangeUserPassword({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_USERS_RESOURCE] });
            toast({
                title: formatMessage(userMessages.changePasswordSuccess),
                description: formatMessage(userMessages.changePasswordSuccessDescription),
            });
            setIsModalOpen(false);
        },
        onError: (error) => onError(error, form.setError),
    });

    const form = useForm<ChangeUserPasswordFormSchemaValues>({
        resolver: zodResolver(changeUserPasswordFormSchema),
        defaultValues,
    });

    // defaultValues are cached and require the reset API to update
    React.useEffect(() => {
        form.reset(defaultValues);
    }, [isModalOpen, form]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const title = formatMessage(userMessages.changePassword);

    const onSubmit = React.useCallback(
        (data: ChangeUserPasswordFormSchemaValues) => mutate({ data, userId: selectedUser.id }),
        [mutate, selectedUser.id],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>{children}</DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.LOCK} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(userMessages.changePasswordSubtitle, {
                                    entry: selectedUser.name,
                                })}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <ChangeUserPasswordForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ChangeUserPasswordFormModal;
